import {
  AlertTriangle,
  Banknote,
  ClipboardList,
  Package,
  ShoppingCart,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PRODUCT_TYPE_LABELS, SEUIL_STOCK_FAIBLE } from '@sirajoptic/shared';
import { BadgeMouvement, BadgeReste, BadgeStatutCommande } from '@/components/ui/badge';
import { Button, variantesBouton } from '@/components/ui/button';
import { Card, CardHeader, StatCard, Tendance } from '@/components/ui/card';
import { ChargementBloc, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { TBody, TD, TH, THead, TR, Table } from '@/components/ui/table';
import { RestockDialog } from '@/features/stock/components/RestockDialog';
import { VenteDialog } from '@/features/ventes/components/VenteDialog';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { date, depuis, dh, entier, nombre, pluriel, tronquer } from '@/lib/format';
import { useDashboard } from './api/dashboard.api';
import { CaChart } from './components/CaChart';
import { RenouvellementsCard } from './components/RenouvellementsCard';

/**
 * Variation d'une mesure, en rapport (`0,12` = +12 %).
 *
 * `null` quand la période de référence est à zéro : une hausse depuis rien n'a
 * pas de pourcentage, et « +∞ % » n'apprend rien à personne.
 */
function variation(actuel: number, precedent: number): number | null {
  if (precedent <= 0) return null;
  return (actuel - precedent) / precedent;
}

export function DashboardPage() {
  const { data, isPending, error, refetch } = useDashboard();
  const naviguer = useNavigate();
  const [vente, setVente] = useState(false);
  const [reapprovisionner, setReapprovisionner] = useState<{ id: string; nom: string } | null>(
    null,
  );

  if (isPending) return <ChargementBloc libelle="Chargement du tableau de bord…" />;
  if (error) return <EtatErreur erreur={error} onReessayer={() => void refetch()} />;

  const { stats, stock_faible, ca_par_mois, top_verres, top_montures, par_societe } = data;

  /* Le mois en cours est incomplet ; la comparaison porte donc sur le même
   * nombre de jours du mois précédent, borné côté serveur. L'infobulle le dit,
   * parce qu'une comparaison dont on ignore le périmètre est un piège. */
  const moisPrecedent = ca_par_mois[ca_par_mois.length - 2]?.libelle;
  const perimetre = `Comparaison à périmètre égal : les ${pluriel(
    stats.jours_ecoules,
    'premier jour',
  )} de chaque mois.`;

  const recouvrement = stats.ca_mois > 0 ? stats.encaisse_mois / stats.ca_mois : 0;
  const cumulAnnuel = ca_par_mois.reduce((somme, p) => somme + p.ca, 0);

  return (
    <>
      <PageHeader
        titre="Tableau de bord"
        description="Vue d'ensemble de l'activité du magasin."
        actions={
          <>
            {/* Une vente au comptoir se fait pendant qu'un client attend : elle
                part d'ici, et aussi de l'écran Ventes. */}
            <Button
              variant="primary"
              icone={<ShoppingCart aria-hidden className="h-4 w-4" />}
              onClick={() => setVente(true)}
            >
              Nouvelle vente
            </Button>
            <Link to="/commandes?nouvelle=1" className={variantesBouton({ variant: 'outline' })}>
              Nouvelle commande
            </Link>
            <Link to="/stock" className={variantesBouton({ variant: 'outline' })}>
              Gérer le stock
            </Link>
          </>
        }
      />

      <PageBody>
        {/* Les commandes à vérifier passent avant les chiffres : ce sont des
            lignes d'import dont on ne sait pas encore si elles sont justes, et
            elles pèsent sur tout ce qui suit. */}
        {stats.commandes_a_verifier > 0 ? (
          <button
            type="button"
            onClick={() => naviguer('/commandes?a_verifier=1')}
            className="mb-3 flex w-full items-center gap-2 rounded-card border border-mustard-300
              bg-mustard-50 px-4 py-3 text-left text-sm text-ink transition-colors hover:bg-mustard-100"
          >
            <AlertTriangle aria-hidden className="h-4 w-4 shrink-0 text-mustard-600" />
            <span>
              <strong className="font-medium">
                {pluriel(stats.commandes_a_verifier, 'commande importée')}
              </strong>{' '}
              {stats.commandes_a_verifier > 1 ? 'demandent' : 'demande'} une vérification.
            </span>
            <span className="ml-auto shrink-0 text-mustard-700">Voir →</span>
          </button>
        ) : null}

        {/* Les mesures d'activité viennent des commandes, pas des factures. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            libelle="CA du mois"
            valeur={dh(stats.ca_mois)}
            icone={<TrendingUp aria-hidden className="h-4 w-4" />}
            accent
            tendance={
              <Tendance
                variation={variation(stats.ca_mois, stats.ca_periode_precedente)}
                libelle={moisPrecedent ? `vs ${moisPrecedent}` : undefined}
                precision={perimetre}
              />
            }
            secondaire={`${pluriel(stats.operations_mois, 'vente')} ce mois-ci`}
            sparkline={ca_par_mois.map((p) => p.ca)}
          />
          <StatCard
            libelle="Encaissé du mois"
            valeur={dh(stats.encaisse_mois)}
            icone={<Banknote aria-hidden className="h-4 w-4" />}
            tendance={
              <Tendance
                variation={variation(stats.encaisse_mois, stats.encaisse_periode_precedente)}
                libelle={moisPrecedent ? `vs ${moisPrecedent}` : undefined}
                precision={perimetre}
              />
            }
            jauge={{
              part: recouvrement,
              libelle: `${nombre(recouvrement * 100, 0)} % du chiffre d'affaires du mois`,
            }}
          />
          <StatCard
            libelle="Total des restes"
            valeur={dh(stats.total_restes)}
            secondaire={pluriel(
              stats.commandes_non_soldees,
              'commande non soldée',
              'commandes non soldées',
            )}
            icone={<Wallet aria-hidden className="h-4 w-4" />}
            onClick={() => naviguer('/commandes?non_soldees=1')}
          />
          <StatCard
            libelle="Valeur du stock"
            valeur={dh(stats.valeur_stock_vente)}
            secondaire={`${dh(stats.valeur_stock_initial)} au prix d'achat · ${pluriel(stats.nombre_articles, 'article')}`}
            icone={<Package aria-hidden className="h-4 w-4" />}
            onClick={() => naviguer('/stock')}
          />
        </div>

        {/* Le graphique prend toute la largeur : douze mois écrasés sur un
            tiers d'écran, les variations d'un mois à l'autre ne se voyaient
            plus. */}
        <Card className="mt-4">
          <CardHeader
            titre="Chiffre d'affaires"
            description="Douze derniers mois — commandes et ventes au comptoir réunies."
            action={
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-ink-muted">Cumul 12 mois</p>
                <p className="text-lg font-semibold tabular text-ink">{dh(cumulAnnuel)}</p>
              </div>
            }
          />
          <CaChart donnees={ca_par_mois} />
        </Card>

        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <RenouvellementsCard lignes={data.renouvellements} total={stats.renouvellements_mois} />

          <Card>
            <CardHeader
              titre="Dernières commandes"
              action={
                <Link
                  to="/commandes"
                  className="text-xs font-medium text-mustard-600 hover:underline"
                >
                  Tout voir
                </Link>
              }
            />
            {data.dernieres_commandes.length === 0 ? (
              <EtatVide
                titre="Aucune commande"
                description="Les commandes créées apparaîtront ici."
              />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Date</TH>
                    <TH>Client</TH>
                    <TH numerique>Total</TH>
                    <TH>Reste</TH>
                    <TH>Statut</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.dernieres_commandes.map((c) => (
                    <TR key={c.id} onClick={() => naviguer(`/commandes?id=${c.id}`)}>
                      <TD className="whitespace-nowrap text-ink-muted">{date(c.date_commande)}</TD>
                      <TD className="font-medium">{tronquer(c.client_nom, 24)}</TD>
                      <TD numerique>{dh(c.total)}</TD>
                      <TD>
                        <BadgeReste reste={c.reste} />
                      </TD>
                      <TD>
                        <BadgeStatutCommande statut={c.statut} />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ListeTop
            titre="Top verres"
            description="Types les plus vendus, 12 derniers mois."
            lignes={top_verres}
          />
          <ListeTop
            titre="Top montures"
            description="Modèles les plus vendus, 12 derniers mois."
            lignes={top_montures}
          />
          <ListeTop
            titre="Par société"
            description="Répartition des fournisseurs, 12 derniers mois."
            lignes={par_societe}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <Card>
            <CardHeader
              titre="Stock faible"
              description={`Articles à ${SEUIL_STOCK_FAIBLE} unités ou moins.`}
              action={
                <Link
                  to="/stock?stock_faible=1"
                  className="text-xs font-medium text-mustard-600 hover:underline"
                >
                  Tout voir
                </Link>
              }
            />
            {stock_faible.length === 0 ? (
              <EtatVide
                titre="Aucun article en alerte"
                description="Tout le stock est au-dessus du seuil."
              />
            ) : (
              <ul className="divide-y divide-line">
                {stock_faible.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{p.nom}</p>
                      <p className="text-xs text-ink-muted">{PRODUCT_TYPE_LABELS[p.type]}</p>
                    </div>
                    <span
                      className={`shrink-0 text-sm font-semibold tabular ${
                        p.quantite === 0 ? 'text-danger' : 'text-mustard-600'
                      }`}
                    >
                      {entier(p.quantite)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setReapprovisionner({ id: p.id, nom: p.nom })}
                      className="shrink-0 rounded-md border border-line px-2 py-1 text-xs
                        font-medium text-ink transition-colors hover:border-mustard-500 hover:bg-mustard-50"
                    >
                      Réassort
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              titre="Derniers mouvements"
              description="Dix dernières variations de stock."
            />
            {data.derniers_mouvements.length === 0 ? (
              <EtatVide
                titre="Aucun mouvement"
                description="Les entrées et sorties de stock s'inscrivent ici."
                icone={<ClipboardList aria-hidden className="h-6 w-6" />}
              />
            ) : (
              <ul className="divide-y divide-line">
                {data.derniers_mouvements.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                    <BadgeMouvement raison={m.raison} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-ink">{m.produit_nom}</p>
                      {m.note ? <p className="truncate text-xs text-ink-muted">{m.note}</p> : null}
                    </div>
                    <span
                      className={`shrink-0 text-sm font-semibold tabular ${
                        m.delta > 0 ? 'text-success' : 'text-danger'
                      }`}
                    >
                      {m.delta > 0 ? '+' : ''}
                      {entier(m.delta)}
                    </span>
                    <span className="w-20 shrink-0 text-right text-xs text-ink-muted">
                      {depuis(m.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </PageBody>

      <RestockDialog produit={reapprovisionner} onFermer={() => setReapprovisionner(null)} />

      <VenteDialog ouvert={vente} onFermer={() => setVente(false)} />
    </>
  );
}

/**
 * Palmarès à barres. Le rang est écrit, parce que sur trois listes côte à côte
 * l'ordre seul se perd, et la part relative dit ce que la barre suggère.
 */
function ListeTop({
  titre,
  description,
  lignes,
}: {
  titre: string;
  description: string;
  lignes: { libelle: string; quantite: number; montant: number }[];
}) {
  const maximum = Math.max(1, ...lignes.map((l) => l.quantite));
  const total = lignes.reduce((somme, l) => somme + l.quantite, 0);

  return (
    <Card>
      <CardHeader titre={titre} description={description} />
      {lignes.length === 0 ? (
        <EtatVide titre="Pas encore de données" />
      ) : (
        <ul className="space-y-2.5 p-4">
          {lignes.map((l, rang) => (
            <li key={l.libelle}>
              <div className="mb-1 flex items-baseline gap-2">
                <span
                  aria-hidden
                  className="w-4 shrink-0 text-xs font-semibold tabular text-ink-muted"
                >
                  {rang + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-ink">{l.libelle}</span>
                <span className="shrink-0 text-xs text-ink-muted tabular">
                  {entier(l.quantite)} · {dh(l.montant)}
                </span>
              </div>
              <div className="ml-6 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-sunken">
                  <div
                    className="h-full rounded-full bg-mustard-500"
                    style={{ width: `${Math.max(4, (l.quantite / maximum) * 100)}%` }}
                  />
                </div>
                <span className="w-9 shrink-0 text-right text-xs text-ink-muted tabular">
                  {total > 0 ? `${nombre((l.quantite / total) * 100, 0)} %` : '—'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
